import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Dropdown, DropdownElement, Input, OptionGroup, PhoneInput } from '@column/column-ui-kit';

import type { IdentificationDocument, PersonEntityFormValues } from '../_util/useEntityForm';
import { AddressField, DateInput, HiddenInput, RenderFields } from '~/components';
import { SectionWithFields } from '~/components/RenderFields';
import { useAddress } from '~/hooks';
import { Grid } from '~/styles';

const useIsCitizenDefaults = (form: ReturnType<typeof useFormContext>, hasId: boolean) => {
  const isCitizen = form.watch('temporaryData.isCitizen');
  const ssn = form.watch('ssn');
  const passport = form.watch('passport.number');
  const driversLicense = form.watch('driversLicense.number');
  const nationalId = form.watch('nationalId.number');

  useEffect(() => {
    if (typeof isCitizen !== 'boolean') {
      if (!hasId) {
        form.setValue('temporaryData.isCitizen', true);
        return;
      }
      if (ssn) {
        form.setValue('temporaryData.isCitizen', true);
      } else if (passport || driversLicense || nationalId) {
        form.setValue('temporaryData.isCitizen', false);
      } else {
        form.setValue('temporaryData.isCitizen', true);
      }
    }
  }, [form, hasId, isCitizen, ssn, passport, driversLicense, nationalId]);
};

export const PersonEntity: React.FC<{
  id?: string;
  disableForm?: boolean;
}> = (props) => {
  const form = useFormContext();
  const { countries, states } = useAddress({ types: ['state', 'country'] });
  const entityAddressStateRef = useRef<DropdownElement>(null);
  const [addressSearchValue, setAddressSearchValue] = useState('');

  useIsCitizenDefaults(form, Boolean(props.id));

  const isCitizen = form.watch('temporaryData.isCitizen') === true;
  const requiresUSAddressRegion = form.watch('address.countryCode') === 'US';

  const sections: SectionWithFields<PersonEntityFormValues, undefined>[] = [
    {
      headline: 'Information',
      fields: [
        {
          id: 'firstName',
          label: 'First Name',
          rules: { required: true },
          children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              placeholder="First Name"
              hasError={isTouched && !!error}
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'middleName',
          label: 'Middle Name',
          children: ({ value, onChange, onBlur, ref }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              placeholder="Middle Name"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'lastName',
          label: 'Last Name',
          rules: { required: true },
          children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              placeholder="Last Name"
              hasError={isTouched && !!error}
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'dateOfBirth',
          label: 'Date of Birth',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <DateInput value={(value as string) ?? ''} onChange={onChange} isDisabled={props.disableForm} />
          ),
        },
        {
          id: 'email',
          label: 'Email Address',
          children: ({ value, onChange, onBlur, ref }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              placeholder="Email Address"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'phoneNumber',
          label: 'Phone Number',
          children: ({ value, onChange }) => (
            <PhoneInput
              value={(value as string) ?? ''}
              onChange={onChange}
              countryList={countries}
              isDisabled={props.disableForm}
              placeholder=""
            />
          ),
        },
        {
          id: 'temporaryData.isCitizen',
          label: 'Is this person a U.S. Citizen?',
          children: ({ value, onChange }) => (
            <OptionGroup orientation={OptionGroup.Orientation.Horizontal}>
              <OptionGroup.Option isActive={value === true} onClick={() => onChange(true)}>
                <OptionGroup.Option.Radio />
                <OptionGroup.Option.Label>Yes</OptionGroup.Option.Label>
              </OptionGroup.Option>
              <OptionGroup.Option isActive={value === false} onClick={() => onChange(false)}>
                <OptionGroup.Option.Radio />
                <OptionGroup.Option.Label>No</OptionGroup.Option.Label>
              </OptionGroup.Option>
            </OptionGroup>
          ),
        },
        {
          id: 'ssn',
          label: 'Social Security Number',
          description: 'ITIN is also acceptable.',
          children: ({ value, onChange, onBlur, ref }) => (
            <HiddenInput
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              placeholder="Social Security Number"
              defaultIsVisible={!props.id}
              readOnly={false}
              showCopy={false}
              visibilityLabel="Social Security Number"
            />
          ),
        },
        {
          id: 'driversLicense',
          label: 'Drivers License',
          hide: isCitizen,
          children: ({ value, onBlur, ref }) => (
            <Grid gap={8}>
              <Input
                value={(value as IdentificationDocument)?.number ?? ''}
                onChange={(val: string) => form.setValue('driversLicense.number', val)}
                placeholder="Drivers License Number"
                onBlur={onBlur}
                ref={ref}
              />
              <Dropdown
                placeholder="Select Country..."
                active={(value as IdentificationDocument)?.countryCode ?? ''}
                onChange={(val: string) => form.setValue('driversLicense.countryCode', val)}
                options={countries
                  .filter((c) => c.code !== 'US' && c.code !== 'CA')
                  .map((c) => ({ label: c.name, value: c.code }))}
                fullWidth
              />
            </Grid>
          ),
        },
        {
          id: 'passport',
          label: 'Passport',
          hide: isCitizen,
          children: ({ value, onBlur, ref }) => (
            <Grid gap={8}>
              <Input
                value={(value as IdentificationDocument)?.number ?? ''}
                onChange={(val: string) => form.setValue('passport.number', val)}
                onBlur={onBlur}
                ref={ref}
                placeholder="Passport Number"
              />
              <Dropdown
                placeholder="Select Country..."
                active={(value as IdentificationDocument)?.countryCode ?? ''}
                onChange={(val: string) => form.setValue('passport.countryCode', val)}
                options={countries.filter((c) => c.code !== 'US').map((c) => ({ label: c.name, value: c.code }))}
                fullWidth
              />
            </Grid>
          ),
        },
        {
          id: 'nationalId',
          label: 'National ID',
          hide: isCitizen,
          children: ({ value, onBlur, ref }) => (
            <Grid gap={8}>
              <Input
                value={(value as IdentificationDocument)?.number ?? ''}
                onChange={(val: string) => form.setValue('nationalId.number', val)}
                onBlur={onBlur}
                ref={ref}
                placeholder="National ID Number"
              />
              <Dropdown
                placeholder="Select Country..."
                active={(value as IdentificationDocument)?.countryCode ?? ''}
                onChange={(val: string) => form.setValue('nationalId.countryCode', val)}
                options={countries
                  .filter((c) => c.code !== 'US' && c.code !== 'CA')
                  .map((c) => ({ label: c.name, value: c.code }))}
                fullWidth
              />
            </Grid>
          ),
        },
      ],
    },
    {
      headline: 'Address',
      fields: [
        {
          id: 'address',
          label: 'Search Address',
          children: () => (
            <AddressField
              onChange={(a) => {
                form.setValue('address.line1', a.line1);
                form.setValue('address.line2', a.line2);
                form.setValue('address.city', a.city);
                form.setValue('address.state', a.state);
                form.setValue('address.postalCode', a.postalCode);
                form.setValue('address.countryCode', a.countryCode);
                setAddressSearchValue('');
                if (!a.state) {
                  entityAddressStateRef.current?.open();
                }
              }}
              justUS
              isDisabled={props.disableForm}
              value={addressSearchValue}
              onValueChange={setAddressSearchValue}
            />
          ),
        },
        {
          id: 'address.line1',
          label: 'Line 1',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Line 1"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.line2',
          label: 'Line 2',
          children: ({ value, onChange }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Line 2"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.city',
          label: 'City',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="City"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.state',
          label: 'State',
          rules: requiresUSAddressRegion ? { required: true } : undefined,
          children: ({ value, onChange }) => {
            const { getValues } = useFormContext();
            const countryCode = getValues('address.countryCode');
            const filteredStates = states.filter((s) => !countryCode || s.country_code === countryCode);
            return (
              <Dropdown
                fullWidth
                ref={entityAddressStateRef}
                search
                placeholder="Select State..."
                active={(value as string) ?? ''}
                onChange={onChange}
                options={filteredStates.map((s) => ({
                  label: `${s.name} (${s.state_code})`,
                  value: s.state_code,
                }))}
                isDisabled={props.disableForm}
              />
            );
          },
        },
        {
          id: 'address.postalCode',
          label: 'Postal Code',
          rules: requiresUSAddressRegion ? { required: true } : undefined,
          children: ({ value, onChange }) => (
            <Input
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Postal Code"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.countryCode',
          label: 'Country',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Dropdown
              fullWidth
              search
              placeholder="Select Country..."
              active={(value as string) ?? ''}
              onChange={onChange}
              options={countries.map((c) => ({ label: c.name, value: c.code }))}
              isDisabled={props.disableForm}
            />
          ),
        },
      ],
    },
  ];

  return <RenderFields sections={sections} />;
};
